import type { GridProps, HTMLChakraProps } from '@chakra-ui/react';
import {
  Box, Grid, Flex, Image, Text, Link,
} from '@chakra-ui/react';
import React from 'react';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';

const Footer = () => {
  const renderProjectInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Box gridArea={ gridArea }>
        <Flex columnGap={ 2 } fontSize="xs" lineHeight={ 5 } alignItems="center" color="text">
          <Box
            as="a"
            href={ route({ pathname: '/' }) }
            width={{ base: '120px' }}
            height={{ base: '24px' }}
            display="inline-flex"
            overflow="hidden"
            flexShrink={ 0 }
            aria-label="Link to main page"
          >
            <Image
              w="auto"
              h="100%"
              src="/static/taostats.svg"
              alt="Taostats logo"
              display={{ base: 'block' }}
            />
          </Box>
        </Flex>
        <Text mt={ 3 } fontSize="xs">
          taostats is a Block Explorer and Analytics Platform for Bittensor, a decentralized machine learning network.
        </Text>
      </Box>
    );
  }, []);

  const containerProps: HTMLChakraProps<'div'> = {
    as: 'footer',
    borderTopWidth: '1px',
    borderTopColor: 'solid',
  };

  const contentProps: GridProps = {
    px: { base: 4, lg: config.UI.navigation.layout === 'horizontal' ? 6 : 12, '2xl': 6 },
    py: { base: 4, lg: 8 },
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
    maxW: `${ CONTENT_MAX_WIDTH }px`,
    m: '0 auto',
  };

  if (config.UI.footer.links) {
    return (
      <Box { ...containerProps }>
        <Grid { ...contentProps }>
          <div>
            { renderProjectInfo() }
          </div>

          <TaostatsFundingInfo/>
          <GPLComplianceNotice/>

        </Grid>
      </Box>
    );
  }

  return (
    <Box { ...containerProps }>
      <Grid
        { ...contentProps }
        gridTemplateAreas={{
          lg: `
          "info funding-info"
        `,
        }}
      >
        { renderProjectInfo({ lg: 'info' }) }

        <Box gridArea={{ lg: 'funding-info' }} justifySelf={{ lg: 'flex-end' }}>
          <TaostatsFundingInfo/>
          <GPLComplianceNotice/>
        </Box>
      </Grid>
    </Box>
  );
};

const TaostatsFundingInfo = () => {
  return (
    <div>
      <Text
        fontSize="xs"
        color="text_secondary"
        textAlign={{ base: 'left', lg: 'right' }}
        mt={{ base: '10px', lg: 0 }}
      >
        taostats is funded by public delegation. Support us by delegating stake to the{ ' ' }
        <Link
          href="https://taostats.io/pro/stake"
          color="#00dbbc"
          textDecoration="underline"
          isExternal
        >
          taostats.io validator
        </Link>
      </Text>
    </div>
  );
};

const GPLComplianceNotice = () => {
  return (
    <Text
      fontSize="xs"
      color="text_secondary"
      mt={ 3 }
      textAlign={{ base: 'left', lg: 'right' }}
    >
      This explorer is based on{ ' ' }
      <Link href="https://github.com/blockscout/blockscout" color="link" textDecoration="underline" isExternal>
        Blockscout
      </Link>
      { ' ' }and is provided under the{ ' ' }
      <Link href="https://www.gnu.org/licenses/gpl-3.0.html" color="link" textDecoration="underline" isExternal>
        GNU GPLv3
      </Link>
      . Copyright (C) Blockscout contributors.
      <br/>
      Taostats includes local modifications and additional analytics features.
    </Text>
  );
};

export default React.memo(Footer);
